import type { ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlarmClock, AlertTriangle, ArrowRight, Inbox, PauseCircle, Ticket, UserCheck } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Skeleton } from '@/components/Skeleton';
import { ApiError } from '@/lib/api';
import { formatarDataHora, formatarDataLonga, formatarDistancia } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/features/auth/auth.store';
import { AvisoDeRetidas } from '@/features/email/AvisoDeRetidas';
import {
  chamadosAbertos,
  chamadosAtribuidosA,
  chamadosPendentes,
  LIMITE_DA_API,
  type ChamadoDoPainel,
  type Contagem,
} from '@/features/home/home.api';
import { ROTULO_DE_NIVEL } from '@/features/tickets/types';
import { minhasTarefas, listarProjetos } from '@/features/projetos/projetos.api';

/**
 * Painel inicial.
 *
 * Os números vêm de `GET /tickets`, que devolve página e **não** total. Por
 * isso todo indicador carrega `parcial`: acima do teto da API o valor vira um
 * mínimo e a tela mostra "200+", em vez de fingir que contou tudo.
 */
export function HomePage() {
  const user = useAuthStore((estado) => estado.user);

  const abertos = useQuery({ queryKey: ['painel', 'abertos'], queryFn: chamadosAbertos });

  const userId = user?.id ?? null;
  const meus = useQuery({
    queryKey: ['painel', 'meus', userId],
    queryFn: async () => {
      if (!userId) throw new Error('A sessão não identifica o usuário.');
      return chamadosAtribuidosA(userId);
    },
    enabled: userId !== null,
  });

  const pendentes = useQuery({ queryKey: ['painel', 'pendentes'], queryFn: chamadosPendentes });

  const minhasAtividades = useQuery({
    queryKey: ['painel', 'minhas-atividades'],
    queryFn: minhasTarefas,
  });

  const projetos = useQuery({
    queryKey: ['painel', 'projetos-ativos'],
    queryFn: () => listarProjetos({ status: 'ACTIVE' }),
  });

  const atrasadas = (minhasAtividades.data ?? []).filter(
    (t) => t.progress < 100 && t.finishAt !== null && t.finishAt < Math.floor(Date.now() / 60_000),
  );

  return (
    <div className="mx-auto max-w-[1400px] animate-slide-up space-y-5">
      <header className="flex items-end justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-base font-semibold">
            {saudacao()}
            {user?.name ? `, ${primeiroNome(user.name)}` : ''}
          </h1>
          {/* `first-letter` e não `capitalize`: o Intl devolve "domingo, 23 de
              agosto", e `capitalize` transformaria o "de" em "De". */}
          <p className="mt-0.5 text-xs text-muted-foreground first-letter:uppercase">
            {formatarDataLonga(new Date())}
          </p>
        </div>
      </header>

      {/*
        A fila de mensagens retidas entra ANTES dos indicadores.
        Um e-mail retido é um cliente que escreveu e ainda não tem chamado —
        pior do que qualquer número abaixo, porque ele não aparece em nenhum
        deles. O cartão some sozinho quando a fila esvazia: ver `AvisoDeRetidas`.
      */}
      <AvisoDeRetidas />

      <section aria-labelledby="indicadores" className="space-y-3">
        <h2 id="indicadores" className="sr-only">
          Indicadores de chamados
        </h2>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <IndicadorCard
            icon={<Inbox className="h-4 w-4" aria-hidden />}
            label="Chamados abertos"
            nota="Somente chamados raiz — subchamado conta dentro do pai."
            consulta={abertos}
            extrair={(dado) => dado.contagem}
          />

          <IndicadorCard
            icon={<AlarmClock className="h-4 w-4" aria-hidden />}
            label="Vencendo em 24 h"
            nota="Calculado a partir do prazo de cada chamado aberto: a API não expõe agregação de SLA."
            consulta={abertos}
            extrair={(dado) => dado.vencendo}
            destaque
          />

          <IndicadorCard
            icon={<UserCheck className="h-4 w-4" aria-hidden />}
            label="Atribuídos a mim"
            nota="Abertos em que você é a pessoa responsável."
            consulta={meus}
            extrair={(dado) => dado}
            indisponivel={userId ? undefined : 'A sessão não identifica o usuário; entre de novo.'}
          />

          <IndicadorCard
            icon={<PauseCircle className="h-4 w-4" aria-hidden />}
            label="Aguardando terceiros"
            nota="Chamados na categoria Pendente, parados à espera de alguém de fora."
            consulta={pendentes}
            extrair={(dado) => dado}
          />
        </div>
      </section>

      <Card>
        <CardHeader
          title="Abertos mais recentes"
          description="Os últimos chamados raiz que entraram na fila."
          action={
            <Link
              to="/chamados"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-accent focus-visible:shadow-glow focus-visible:outline-none"
            >
              Ver todos
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          }
        />
        <CardBody>
          <ListaDeRecentes consulta={abertos} />
        </CardBody>
      </Card>

      {atrasadas.length > 0 && (
        <section aria-labelledby="atrasadas-heading" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 id="atrasadas-heading" className="text-sm font-semibold text-destructive flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Minhas tarefas atrasadas
            </h2>
            <Link to="/minhas-atividades" className="text-xs text-muted-foreground hover:text-foreground">
              Ver todas →
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {atrasadas.slice(0, 6).map((t) => (
              <Link
                key={t.id}
                to="/projetos/$projetoId"
                params={{ projetoId: t.project.id }}
                className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 hover:bg-destructive/10 transition-colors"
              >
                <p className="text-xs font-mono text-muted-foreground">{t.outlineNumber}</p>
                <p className="text-sm font-medium text-destructive line-clamp-1 mt-0.5">{t.name}</p>
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{t.project.number}</span>
                  {t.finishAt && (
                    <span className="text-destructive font-medium">
                      Prazo: {new Date((t.finishAt - 1) * 60_000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {(projetos.data?.items?.length ?? 0) > 0 && (
        <section aria-labelledby="projetos-heading" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 id="projetos-heading" className="text-sm font-semibold">Projetos ativos</h2>
            <Link to="/projetos" className="text-xs text-muted-foreground hover:text-foreground">
              Ver todos →
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {projetos.data!.items.slice(0, 6).map((p) => {
              const pct = p.progressPercent ?? 0;
              return (
                <Link
                  key={p.id}
                  to="/projetos/$projetoId"
                  params={{ projetoId: p.id }}
                  className="rounded-lg border border-border bg-background p-3 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-mono text-muted-foreground">{p.number}</p>
                      <p className="text-sm font-medium line-clamp-1 group-hover:text-primary transition-colors">{p.name}</p>
                    </div>
                    <span className="text-xs font-semibold tabular-nums text-muted-foreground shrink-0">{pct}%</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full transition-all', pct === 100 ? 'bg-green-500' : 'bg-primary')}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ── indicadores ────────────────────────────────────────────────────────────

interface IndicadorProps<T> {
  icon: ReactNode;
  label: string;
  nota: string;
  consulta: UseQueryResult<T, Error>;
  extrair: (dado: T) => Contagem;
  /** Motivo pelo qual o número não pode ser calculado — vence a consulta. */
  indisponivel?: string | undefined;
  destaque?: boolean;
}

function IndicadorCard<T>({
  icon,
  label,
  nota,
  consulta,
  extrair,
  indisponivel,
  destaque = false,
}: IndicadorProps<T>) {
  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-card-hover">
      <div className="flex h-full">
        <div
          aria-hidden
          className={cn(
            'w-[3px] flex-none self-stretch',
            destaque ? 'bg-warning' : 'bg-primary',
          )}
        />
        <div className="flex-1 space-y-2.5 px-4 py-4">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                'flex-none opacity-70',
                destaque ? 'text-warning' : 'text-primary',
              )}
            >
              {icon}
            </span>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
          </div>

          <ValorDoIndicador
            consulta={consulta}
            extrair={extrair}
            indisponivel={indisponivel}
            label={label}
          />

          <p className="text-[11px] leading-snug text-muted-foreground/60" title={nota}>
            {nota}
          </p>
        </div>
      </div>
    </Card>
  );
}

function ValorDoIndicador<T>({
  consulta,
  extrair,
  indisponivel,
  label,
}: Pick<IndicadorProps<T>, 'consulta' | 'extrair' | 'indisponivel' | 'label'>) {
  if (indisponivel) {
    return (
      <div>
        <p className="text-3xl font-semibold tabular-nums text-muted-foreground/60">—</p>
        <p className="text-xs text-warning">{indisponivel}</p>
      </div>
    );
  }

  if (consulta.isPending) {
    return (
      <div role="status" aria-busy aria-live="polite">
        <span className="sr-only">Carregando {label}</span>
        <Skeleton className="h-9 w-16" />
      </div>
    );
  }

  if (consulta.isError) {
    const semPermissao = consulta.error instanceof ApiError && consulta.error.status === 403;
    return (
      <div>
        <p className="text-3xl font-semibold tabular-nums text-muted-foreground/60">—</p>
        <p className="text-xs text-destructive">
          {semPermissao ? 'Seu perfil não vê chamados.' : 'Não foi possível consultar agora.'}
        </p>
      </div>
    );
  }

  const contagem = extrair(consulta.data);

  return (
    <div>
      <p className="text-4xl font-bold tabular-nums tracking-tight">
        {contagem.valor}
        {contagem.parcial ? <span className="text-2xl font-semibold text-muted-foreground">+</span> : null}
      </p>
      {contagem.parcial ? (
        <p className="text-[11px] text-muted-foreground/60">
          Mínimo — API limita a {LIMITE_DA_API} por página.
        </p>
      ) : null}
    </div>
  );
}

// ── lista de recentes ──────────────────────────────────────────────────────

const COR_DE_PRIORIDADE: Record<ChamadoDoPainel['priority'], string> = {
  LOW: 'bg-muted-foreground/40',
  MEDIUM: 'bg-primary',
  HIGH: 'bg-warning',
  CRITICAL: 'bg-destructive',
};


function ListaDeRecentes({
  consulta,
}: {
  consulta: UseQueryResult<{ recentes: ChamadoDoPainel[] }, Error>;
}) {
  if (consulta.isPending) {
    return (
      <div role="status" aria-busy aria-live="polite" className="space-y-3">
        <span className="sr-only">Carregando chamados recentes</span>
        {Array.from({ length: 4 }, (_, indice) => (
          <div key={indice} className="flex items-center gap-3">
            <Skeleton className="h-2 w-2 rounded-full" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (consulta.isError) {
    return <ErrorState error={consulta.error} onRetry={() => void consulta.refetch()} />;
  }

  if (consulta.data.recentes.length === 0) {
    return (
      <EmptyState
        icon={<Ticket className="h-6 w-6" aria-hidden />}
        title="Nenhum chamado aberto"
        description="Ou a fila está limpa, ou nada foi criado ainda neste ambiente."
      />
    );
  }

  return (
    <ul className="divide-y divide-border">
      {consulta.data.recentes.map((chamado) => (
        <li key={chamado.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
          <span
            className={cn('h-2 w-2 flex-none rounded-full', COR_DE_PRIORIDADE[chamado.priority])}
            aria-hidden
          />
          <span className="sr-only">{ROTULO_DE_NIVEL[chamado.priority]}</span>
          <span className="flex-none font-mono text-xs text-muted-foreground">
            {chamado.number}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm">{chamado.subject}</span>
          <span
            className="flex-none text-xs text-muted-foreground"
            title={formatarDataHora(chamado.openedAt)}
          >
            {formatarDistancia(chamado.openedAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── auxiliares ─────────────────────────────────────────────────────────────

function saudacao(agora = new Date()): string {
  const hora = agora.getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}
